import React from "react";
import { useGuide } from "@/contexts/GuideContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DEMO_HINTS } from "@/lib/demoHints";

interface DemoHintProps {
  /** Key into DEMO_HINTS registry, or raw hint string */
  hintKey?: string;
  hint?: string;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** If true, show a small pulsing indicator dot */
  showIndicator?: boolean;
}

export function DemoHint({
  hintKey,
  hint,
  children,
  side = "bottom",
  showIndicator = false,
}: DemoHintProps) {
  const { guideEnabled } = useGuide();

  const text = hint || (hintKey ? DEMO_HINTS[hintKey] : undefined);

  if (!guideEnabled || !text) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="relative inline-flex items-center">
            {children}
            {showIndicator && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal-deep opacity-40" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal-deep opacity-70" />
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-xs text-xs font-normal leading-relaxed bg-foreground text-background border-none shadow-lg"
        >
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
